import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { AdNetworkStatusEnum, type AdNetwork } from '@segmently/schema-models/ad-creative';
import { StyledTextField } from '@modules/ui-components';
import { useAuth } from '@frontend/contexts/AuthContext';
import { AdCreativeApiClient, type UpsertAdCreativeNetworkRequest } from '../services/AdCreativeApiClient';
import { AD_CREATIVE_ADMIN } from '../selectors';

const STATUS_OPTIONS = Object.values(AdNetworkStatusEnum);

const EMPTY_DRAFT: UpsertAdCreativeNetworkRequest = {
  id: '',
  displayName: '',
  provider: '',
  status: AdNetworkStatusEnum.Draft,
  policySummary: '',
  defaultPlacementIds: [],
  isActive: true,
};

export function AdminAdCreativePlatformsPage(): React.ReactElement {
  const { adminLoaded, isAdmin } = useAuth();
  const client = useMemo(() => new AdCreativeApiClient(), []);
  const [networks, setNetworks] = useState<AdNetwork[]>([]);
  const [draft, setDraft] = useState<UpsertAdCreativeNetworkRequest>(EMPTY_DRAFT);
  const [placementsRaw, setPlacementsRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (adminLoaded && isAdmin) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminLoaded, isAdmin]);

  if (!adminLoaded) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/all-projects" replace />;
  }

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    const response = await client.listNetworks();
    if (!response.success || !response.data) {
      setError(response.error || 'Failed to load ad platforms');
      setLoading(false);
      return;
    }
    setNetworks(response.data.networks);
    if (!draft.id && response.data.networks[0]) {
      selectNetwork(response.data.networks[0]);
    }
    setLoading(false);
  }

  function selectNetwork(network: AdNetwork): void {
    setDraft({
      id: network.id,
      displayName: network.displayName,
      provider: network.provider || '',
      icon: network.icon || '',
      status: network.status || AdNetworkStatusEnum.Draft,
      policySummary: network.policySummary || '',
      defaultFormatIds: network.defaultFormatIds || [],
      defaultPlacementIds: network.defaultPlacementIds || [],
      isActive: network.isActive !== false,
    });
    setPlacementsRaw((network.defaultPlacementIds || []).join(', '));
  }

  function startNew(): void {
    setDraft(EMPTY_DRAFT);
    setPlacementsRaw('');
    setStatus(null);
  }

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    const request: UpsertAdCreativeNetworkRequest = {
      ...draft,
      defaultPlacementIds: splitList(placementsRaw),
    };
    const response = await client.upsertNetwork(request);
    if (!response.success || !response.data) {
      setError(response.error || 'Failed to save ad platform');
      setSaving(false);
      return;
    }
    setStatus(`Saved ${response.data.network.displayName}`);
    selectNetwork(response.data.network);
    const listResponse = await client.listNetworks();
    if (listResponse.success && listResponse.data) setNetworks(listResponse.data.networks);
    setSaving(false);
  }

  return (
    <Box data-testid={AD_CREATIVE_ADMIN.page} sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Ad Platforms
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={startNew}>
            New
          </Button>
          <Button data-testid={AD_CREATIVE_ADMIN.refresh} variant="outlined" startIcon={<RefreshIcon />} onClick={() => void refresh()}>
            Refresh
          </Button>
          <Button
            data-testid={AD_CREATIVE_ADMIN.save}
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving || !draft.id || !draft.displayName}
            onClick={() => void save()}
          >
            Save
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
        {status && <Alert data-testid={AD_CREATIVE_ADMIN.statusMessage} severity="success">{status}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 0.8fr) minmax(0, 1.2fr)' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
            <Stack sx={{ p: 1.5 }} spacing={1}>
              <Typography variant="h6">Networks</Typography>
              <Divider />
            </Stack>
            {loading ? (
              <Stack alignItems="center" sx={{ py: 5 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <List data-testid={AD_CREATIVE_ADMIN.networkList} disablePadding>
                {networks.map((network) => (
                  <ListItemButton
                    key={network.id}
                    data-testid={AD_CREATIVE_ADMIN.network(network.id)}
                    selected={draft.id === network.id}
                    onClick={() => selectNetwork(network)}
                  >
                    <ListItemText
                      primary={network.displayName}
                      secondary={network.id}
                    />
                    <Chip size="small" label={network.status || 'draft'} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Settings</Typography>
              <StyledTextField
                data-testid={AD_CREATIVE_ADMIN.id}
                label="Network ID"
                value={draft.id}
                onChange={(value) => setDraft((current) => ({ ...current, id: String(value) }))}
                fullWidth
              />
              <StyledTextField
                data-testid={AD_CREATIVE_ADMIN.displayName}
                label="Display Name"
                value={draft.displayName}
                onChange={(value) => setDraft((current) => ({ ...current, displayName: String(value) }))}
                fullWidth
              />
              <StyledTextField
                data-testid={AD_CREATIVE_ADMIN.provider}
                label="Provider"
                value={draft.provider || ''}
                onChange={(value) => setDraft((current) => ({ ...current, provider: String(value) }))}
                fullWidth
              />
              <StyledTextField
                data-testid={AD_CREATIVE_ADMIN.status}
                label="Status"
                select
                value={draft.status || AdNetworkStatusEnum.Draft}
                onChange={(value) => setDraft((current) => ({ ...current, status: String(value) as AdNetwork['status'] }))}
                fullWidth
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </StyledTextField>
              <StyledTextField
                data-testid={AD_CREATIVE_ADMIN.placements}
                label="Default Placements"
                value={placementsRaw}
                onChange={(value) => setPlacementsRaw(String(value))}
                fullWidth
              />
              <StyledTextField
                data-testid={AD_CREATIVE_ADMIN.policySummary}
                label="Policy Summary"
                value={draft.policySummary || ''}
                onChange={(value) => setDraft((current) => ({ ...current, policySummary: String(value) }))}
                fullWidth
                multiline
                minRows={4}
              />
              <FormControlLabel
                control={
                  <Switch
                    data-testid={AD_CREATIVE_ADMIN.active}
                    checked={draft.isActive !== false}
                    onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
                  />
                }
                label="Active"
              />
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}
